import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Search, Plus, Minus, FileText, Package, History } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BinCardPage() {
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [binCardHistory, setBinCardHistory] = useState([]);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    filterItems();
  }, [items, searchQuery]);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/items`, { headers });
      if (response.ok) {
        const data = await response.json();
        // Filter to show items with bin_card_stock > 0 or raw materials (RMT)
        const binCardItems = data.filter(item => 
          item.bin_card_stock > 0 || 
          item.category?.toUpperCase() === 'RMT'
        );
        setItems(binCardItems);
      }
    } catch (error) {
      console.error('Error fetching items:', error);
      toast.error('Failed to fetch items');
    } finally {
      setLoading(false);
    }
  };

  const filterItems = () => {
    if (!searchQuery.trim()) {
      setFilteredItems(items);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = items.filter(item =>
      (item.item_code || '').toLowerCase().includes(query) ||
      (item.name || '').toLowerCase().includes(query) ||
      (item.item_name || '').toLowerCase().includes(query)
    );
    setFilteredItems(filtered);
  };

  const fetchItemHistory = async (item) => {
    try {
      setSelectedItem(item);
      const response = await fetch(`${API}/api/inventory/movements?item_id=${item.id}`, { headers });
      if (response.ok) {
        const data = await response.json();
        setBinCardHistory(data.slice(0, 50)); // Last 50 movements
      }
      setHistoryDialogOpen(true);
    } catch (error) {
      console.error('Error fetching history:', error);
      toast.error('Failed to fetch item history');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB'); // DD/MM/YYYY
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="bin-card-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bin Card</h1>
            <p className="text-gray-500">View raw material stock levels and history</p>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Package className="h-8 w-8 text-blue-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Items</p>
                  <p className="text-2xl font-bold">{items.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Plus className="h-8 w-8 text-green-500" />
                <div>
                  <p className="text-sm text-gray-500">In Stock</p>
                  <p className="text-2xl font-bold">{items.filter(i => (i.bin_card_stock || 0) > 0).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Minus className="h-8 w-8 text-red-500" />
                <div>
                  <p className="text-sm text-gray-500">Out of Stock</p>
                  <p className="text-2xl font-bold">{items.filter(i => (i.bin_card_stock || 0) <= 0).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <FileText className="h-8 w-8 text-purple-500" />
                <div>
                  <p className="text-sm text-gray-500">Total Bin Stock</p>
                  <p className="text-2xl font-bold">{items.reduce((sum, i) => sum + (i.bin_card_stock || 0), 0).toFixed(2)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Search by item code or name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="bin-card-search"
            />
          </div>
        </div>

        {/* Items Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Bin Card Stock</TableHead>
                  <TableHead className="text-right">Stock in Hand</TableHead>
                  <TableHead className="text-right">Blocked</TableHead>
                  <TableHead>Unit</TableHead>
                  <TableHead className="text-center">History</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : filteredItems.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">No items found</TableCell>
                  </TableRow>
                ) : (
                  filteredItems.map((item) => (
                    <TableRow key={item.id} data-testid={`bin-card-row-${item.id}`}>
                      <TableCell className="font-medium">{item.name || item.item_name || '-'}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="capitalize">
                          {item.category?.replace('_', ' ') || '-'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        <span className={(item.bin_card_stock || 0) > 0 ? 'text-green-600' : 'text-red-500'}>
                          {(item.bin_card_stock || 0).toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{(item.stock_in_hand || 0).toFixed(2)}</TableCell>
                      <TableCell className="text-right">{(item.blocked || 0).toFixed(2)}</TableCell>
                      <TableCell>{item.unit || 'NOS'}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchItemHistory(item)}
                          data-testid={`history-btn-${item.item_code}`}
                        >
                          <History className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* History Dialog */}
        <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-auto">
            <DialogHeader>
              <DialogTitle>
                Bin Card History - {selectedItem?.name || selectedItem?.item_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {selectedItem && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-medium">{selectedItem.name || selectedItem.item_name}</p>
                  <p className="text-sm text-gray-500">
                    Current Stock: {(selectedItem.bin_card_stock || 0).toFixed(2)} {selectedItem.unit || 'NOS'}
                  </p>
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Quantity</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Remarks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {binCardHistory.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-4">No history found</TableCell>
                    </TableRow>
                  ) : (
                    binCardHistory.map((entry, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{formatDate(entry.created_at)}</TableCell>
                        <TableCell>
                          <Badge variant={entry.quantity > 0 ? 'default' : 'destructive'}>
                            {entry.movement_type?.replace('_', ' ') || (entry.quantity > 0 ? 'IN' : 'OUT')}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-right font-medium ${entry.quantity > 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {entry.quantity > 0 ? '+' : ''}{entry.quantity?.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-xs">{entry.reference_id || '-'}</TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">{entry.remarks || '-'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
